/*
 * rta-line-spec.js — the size and color every cart line carries.
 *
 * 1. RTADims.parse() reads a variant's standard dimensions from what the catalog
 *    actually holds (there are no dimension metafields): dimensions labeled in
 *    the variant title, then the SKU size code, then a single labeled value in
 *    the product title, then the standard for the cabinet type. format() turns
 *    them into the one Size line the shopper, checkout, the order email and the
 *    invoice all read:
 *        15" W × 34-1/2" H × 24" D
 *        13-1/2" W (custom) × 34-1/2" H × 24" D     <- reduced by cabinet-sizing
 *        14" W × 39" H × 1/4" Thick                  <- skin panels
 *        4-1/2" H × 93" L × 3/16" Thick              <- toe kick
 *
 * 2. Add-to-cart: every product form gets properties[Size] (the standard size)
 *    and, for standard-finish products with no color picker, properties[Color]
 *    from the custom.color / custom.finish metafield. Product data comes from
 *    snippets/rta-line-spec-data.liquid. A Size written by the cabinet-sizing
 *    section (a reduced dimension) is never overwritten. Written at submit (the
 *    capture listener on <body> runs after the color/size tools' own capture
 *    listeners on document) and kept current on the form, so "Buy it now" —
 *    which reads the form without submitting it — carries them too.
 *
 * 3. Cart fallback: lines added before Size existed, or holding the retired
 *    "Standard" / "Wide - …" text, are rendered by cabinet-line-spec with
 *    data-rta-size-* attributes; they are rewritten here into W × H × D.
 */
(function () {
  'use strict';

  // ---- numbers --------------------------------------------------------------
  var MEAS = '(\\d+(?:\\.\\d+)?(?:\\s*-\\s*|\\s+)\\d+\\/\\d+|\\d+\\/\\d+|\\d+(?:\\.\\d+)?)';
  var QUOTE = '\\s*(?:["”″\'′]{1,2})?';

  function toNum(s) {
    s = String(s).replace(/\s+/g, ' ').trim();
    var m = s.match(/^(\d+)\s*[- ]\s*(\d+)\/(\d+)$/);
    if (m) return +m[1] + m[2] / m[3];
    m = s.match(/^(\d+)\/(\d+)$/);
    if (m) return m[1] / m[2];
    return parseFloat(s);
  }

  // 34.5 -> 34-1/2, 0.1875 -> 3/16, 31.5 -> 31-1/2 (nearest 1/16)
  function frac(n) {
    var sixteenths = Math.round(n * 16);
    var whole = Math.floor(sixteenths / 16), rem = sixteenths % 16, den = 16;
    while (rem && rem % 2 === 0) { rem /= 2; den /= 2; }
    if (!rem) return String(whole);
    return (whole ? whole + '-' : '') + rem + '/' + den;
  }
  function inch(n) { return frac(n) + '"'; }

  // ---- extraction -----------------------------------------------------------
  var TAG = { wide: 'w', width: 'w', w: 'w', tall: 'h', high: 'h', height: 'h', h: 'h', deep: 'd', depth: 'd', d: 'd' };

  // Labeled dimensions: 24" Wide, 30" W, 12" Tall, 21" Deep. In a product title a
  // label that ends a list ("24", 27" & 30" Wide") names many sizes, not this
  // variant's, so list members are skipped when skipLists is set.
  function tagged(text, skipLists) {
    var out = {}, seen = {};
    var re = new RegExp(MEAS + QUOTE + '\\s*-?\\s*(wide|width|w|tall|high|height|h|deep|depth|d)(?![a-z])', 'gi');
    var m;
    while ((m = re.exec(text))) {
      var key = TAG[m[2].toLowerCase()];
      var before = text.slice(Math.max(0, m.index - 6), m.index);
      if (skipLists && (/(?:,|&|\band|\bto)\s*$/i.test(before) || /\d["”″']?\s*[-–]\s*$/.test(before))) { seen[key] = 'list'; continue; }
      var val = toNum(m[1]);
      if (!(val > 0)) continue;
      if (seen[key] && seen[key] !== val) { seen[key] = 'conflict'; continue; }
      seen[key] = val;
    }
    for (var k in seen) if (typeof seen[k] === 'number') out[k] = seen[k];
    return out;
  }

  function measures(text) {
    var re = new RegExp(MEAS + QUOTE, 'g'), out = [], m;
    while ((m = re.exec(text))) out.push(toNum(m[1]));
    return out;
  }

  // SKU size codes. A line prefix may sit in front (D1, Custom, CI-, ROS17 , WS1-1/4"-).
  function skuTokens(sku) {
    var s = String(sku || '').toUpperCase(), m, out = {};
    if (/\bPLUS\b/.test(s)) return out;                       // combo SKUs (wall + box) describe two pieces
    m = s.match(/(?:^|[^A-Z]|CUSTOM)(W(?:DC|AC|BC|MC|GD|A|G)?)\s*(\d{2}(?:\.\d)?|\d(?=\d{4}\b))X?(\d{2})(\d{2})?(?!\d)/);
    if (m) {
      out.family = m[1] === 'W' || m[1] === 'WG' || m[1] === 'WGD' || m[1] === 'WMC' ? 'W' : m[1];
      out.w = parseFloat(m[2]); out.h = parseInt(m[3], 10);
      if (m[4]) out.d = parseInt(m[4], 10);
      if (!(out.w >= 6 && out.w <= 60 && out.h >= 9 && out.h <= 96)) return {};
      return out;
    }
    m = s.match(/(?:^|[^A-Z])(PD?C)(\d{2})(\d{2})(?!\d)/);
    if (m) return { family: 'PC', w: +m[2], h: +m[3] };
    m = s.match(/(?:^|[^A-Z])OC\s*(\d{2})\s*(\d{2})(?!\d)/);
    if (m) return { family: 'OC', w: +m[1] === 31 ? 31.5 : +m[1], h: +m[2] };
    return out;
  }

  // Trim, fillers, samples, hardware: not cabinets, no W × H × D to state.
  var NOT_A_CABINET = /filler|molding|moulding|trim piece|crown|light rail|scribe|quarter round|outside corner|sample|touch ?up|roll ?out shelf|false (door|panel|front)|decorative|end panel|face ?frame|hardware|trash ?can roll|paint\b|valance|corbel/;

  function parse(o) {
    // Normalize the catalog's variant-title typos: 4"-1/2" -> 4-1/2", 1/4'" -> 1/4"
    var vt = String(o.variantTitle || '').replace(/(\d)\s*["”″]\s*-\s*(\d+\/\d+)/g, '$1-$2').replace(/'"/g, '"');
    if (/^default title$/i.test(vt)) vt = '';
    var pt = String(o.productTitle || ''), ty = String(o.productType || '');
    var low = (pt + ' ' + ty).toLowerCase();

    // Toe kick: H x Thickness x Length in any order — thickness is the value under 1".
    if (/toe\s*kick/.test(low) && !/filler|base cabinet/.test(low)) {
      var tk = measures(vt);
      if (tk.length >= 3) {
        var t = Math.min.apply(null, tk), l = Math.max.apply(null, tk);
        var h = tk.filter(function (x) { return x !== t && x !== l; })[0];
        if (t < 1 && h) return { kind: 'toekick', h: h, l: l, t: t };
      }
      return null;
    }

    // Skin panels: W x H, thickness is the value under 1" (1/4", 1/2" thick).
    // Stated smaller × larger, so "96 x 24" and "24 x 96" read the same way.
    if (/skin panel/.test(low) || /(side|back|fridge) panel|x\s*[\d\/-]+["”']*\s*thick/i.test(vt)) {
      var body = vt.replace(/(with|plus|and)\s.*$/i, '');
      var nums = measures(body);
      var thick = nums.filter(function (x) { return x < 1; })[0];
      if (!thick) thick = measures(String(o.sku || '').replace(/\*/g, ' x ')).filter(function (x) { return x < 1; })[0];
      var big = nums.filter(function (x) { return x >= 1; }).slice(0, 2).sort(function (a, b) { return a - b; });
      if (thick && big.length === 2) return { kind: 'panel', w: big[0], h: big[1], t: thick };
      return null;
    }

    // False panels (with frame): W x H in the variant title, always 1" thick.
    // "23" x 84" … - Actual 79.5"" is built to the actual height. The false
    // door molding variant has no W x H pair and stays a standard item.
    if (/false (door|panel)/.test(low + ' ' + vt.toLowerCase())) {
      var fp = vt.match(new RegExp('^\\s*' + MEAS + QUOTE + '\\s*[x×]\\s*' + MEAS + QUOTE));
      if (!fp) return null;
      var fpW = toNum(fp[1]), fpH = toNum(fp[2]);
      var act = vt.match(new RegExp('actual\\s*' + MEAS, 'i'));
      if (act) fpH = toNum(act[1]);
      if (!(fpW > 0 && fpH > 0)) return null;
      return { kind: 'falsepanel', family: 'FP', w: fpW, h: fpH, t: 1 };
    }

    if (NOT_A_CABINET.test(low)) return null;

    var v = tagged(vt, false);
    var tok = skuTokens(o.sku);
    var p = tagged(pt, true);

    // "33" x 84"" (oven) / "18" / 84"" — an untagged W, H pair in the variant title
    var pair = vt.match(new RegExp('^\\s*' + MEAS + QUOTE + '\\s*[x×\\/]\\s*' + MEAS + QUOTE));
    if (pair && v.w == null && v.h == null && toNum(pair[2]) >= 1) {
      var a = toNum(pair[1]), b = toNum(pair[2]);
      if (b >= 9) { v.w = a; v.h = b; } else { v.w = a; }
    }

    var w = v.w != null ? v.w : tok.w != null ? tok.w : p.w;
    var hh = v.h != null ? v.h : tok.h != null ? tok.h : p.h;
    var d = v.d != null ? v.d : tok.d != null ? tok.d : p.d;
    if (w == null) {
      var bt = String(o.sku || '').toUpperCase().match(/(?:^|[^A-Z])(?:B|SB|DB|BBC|LS|VSB|VDB|BT|BP|BSR)(\d{2})(?!\d)/);
      if (bt) w = +bt[1];
    }
    if (w == null) {
      var first = measures(vt).filter(function (x) { return x >= 6 && x <= 60; })[0];
      if (first != null) w = first;
    }

    var baseInVariant = /\bbase\b/i.test(vt);
    var isWall = !baseInVariant && (!!tok.family && tok.family !== 'PC' && tok.family !== 'OC' ||
      /wall cabinet|bridge|stack|upper|over stove|wall (angle|diagonal|blind|microwave)|microwave (shelf )?cabinet wall|glass.ready wall|wall oven wall/.test(low) && !/base cabinet|full height base/.test(low));
    var kind;
    if (hh != null && hh >= 60) kind = 'tall';
    else if (tok.family === 'PC' || tok.family === 'OC' || /pantry(?! base| wall)|tall cabinet|wall oven(?! wall)/.test(low) && !isWall) kind = 'tall';
    else if (isWall) kind = 'wall';
    else kind = 'base';

    if (kind === 'base') { if (hh == null) hh = 34.5; if (d == null) d = 24; }
    if (kind === 'tall' && d == null) d = 24;
    // Wall depth when neither the SKU nor a title states it: inset lines are
    // built 14" deep, overlay lines 12" (both as stated elsewhere in the catalog).
    if (kind === 'wall' && d == null) d = /inset/.test(low) ? 14 : 12;

    if (!(w > 0 && hh > 0 && d > 0)) return null;
    return { kind: kind, family: tok.family || null, w: w, h: hh, d: d };
  }

  function format(spec, custom) {
    if (!spec) return '';
    custom = custom || {};
    function dim(val, label, key) { return inch(val) + ' ' + label + (custom[key] ? ' (custom)' : ''); }
    if (spec.kind === 'panel' || spec.kind === 'falsepanel') return [dim(spec.w, 'W', 'w'), dim(spec.h, 'H', 'h'), inch(spec.t) + ' Thick'].join(' × ');
    if (spec.kind === 'toekick') return [inch(spec.h) + ' H', inch(spec.l) + ' L', inch(spec.t) + ' Thick'].join(' × ');
    return [dim(spec.w, 'W', 'w'), dim(spec.h, 'H', 'h'), dim(spec.d, 'D', 'd')].join(' × ');
  }

  // Retired "Wide - 13-1/2" Tall - 34-1/2" Depth - 24"" -> W × H × D, marking the
  // dimensions that differ from the standard size as custom.
  function fromLegacy(text, std) {
    var m = String(text || '').match(new RegExp('Wide\\s*-\\s*' + MEAS + QUOTE + '(?:\\s*Tall\\s*-\\s*' + MEAS + QUOTE + ')?(?:\\s*Depth\\s*-\\s*' + MEAS + QUOTE + ')?', 'i'));
    if (!m) return null;
    var spec = { kind: std ? std.kind : 'base', w: toNum(m[1]), h: m[2] ? toNum(m[2]) : std && std.h, d: m[3] ? toNum(m[3]) : std && std.d };
    if (!(spec.h && spec.d)) return null;
    return format(spec, std ? { w: spec.w !== std.w, h: spec.h !== std.h, d: spec.d !== std.d } : {});
  }

  // The SKU a made-to-size cabinet is built to: the cabinet's family letters,
  // then its ordered dimensions. The line prefix ("Custom", "CI-") and any
  // trailing words are dropped; a drawer-count suffix (-2D) is kept.
  //   wall    W + W + H + D   W301721   (30 W, 17 H, 21 D)
  //   pantry  PC + W + H      PC1884
  //   other   letters + W     B17, DB17-2D
  //   false panel FP + W + H  FP1027
  // Whole inches are two digits run together; once any dimension has a
  // fraction the values are decimals joined by X: W29.75X17.5X21, B17.25.
  function code(spec, sku, size) {
    if (!spec || spec.kind === 'panel' || spec.kind === 'toekick') return '';
    size = size || spec;
    var s = String(sku || '').toUpperCase(), fam = '', suffix = '', dims;
    var tok = skuTokens(s);
    if (spec.kind === 'falsepanel') {
      fam = 'FP';
      dims = [size.w, size.h];
    } else if (tok.family === 'PC') {
      fam = 'PC';
      dims = [size.w, size.h];
    } else if (spec.kind === 'wall' || tok.family) {
      fam = tok.family && tok.family !== 'OC' ? tok.family : 'W';
      dims = [size.w, size.h, size.d];
    } else {
      var m = s.match(/(?:^|[^A-Z])([A-Z]{1,4})\s*\d{2}(?!\d)(-\d+D(?![A-Z]))?/);
      if (m && m[1] !== 'CI') { fam = m[1]; suffix = m[2] || ''; }
      else if (spec.kind === 'base') fam = 'B';
      else return '';
      dims = [size.w];
    }
    if (dims.some(function (n) { return !(n > 0); })) return '';
    var fractional = dims.some(function (n) { return Math.abs(n - Math.round(n)) > 1e-6; });
    var out = dims.map(function (n) {
      if (fractional) return String(Math.round(n * 1000) / 1000);
      var r = Math.round(n);
      return r < 10 ? '0' + r : String(r);
    }).join(fractional ? 'X' : '');
    return fam + out + suffix;
  }

  // The one size line for a made-to-size cabinet or false panel:
  //   W301721 - Width 30, Height 17, Depth 21
  //   FP1027 - Width 10, Height 27, Thickness 1
  function codeLine(skuCode, size) {
    if (!skuCode || !size) return '';
    var third = size.t != null ? ', Thickness ' + frac(size.t) : ', Depth ' + frac(size.d);
    return skuCode + ' - Width ' + frac(size.w) + ', Height ' + frac(size.h) + third;
  }

  var RTADims = { parse: parse, format: format, frac: frac, fromLegacy: fromLegacy, code: code, codeLine: codeLine };
  window.RTADims = RTADims;
  if (typeof document === 'undefined') {                 // node: catalog tests
    if (typeof module !== 'undefined') module.exports = RTADims;
    return;
  }

  // ---- add-to-cart ----------------------------------------------------------
  var data = null;
  function catalog() {
    if (data) return data;
    data = {};
    document.querySelectorAll('script[data-rta-line-spec]').forEach(function (el) {
      try {
        var p = JSON.parse(el.textContent);
        Object.keys(p.v || {}).forEach(function (id) {
          data[id] = { sku: p.v[id][0], vt: p.v[id][1], pt: p.pt, ty: p.ty, color: typeof p.color === 'string' ? p.color.trim() : '' };
        });
      } catch (e) {}
    });
    return data;
  }

  function input(form, name) {
    var el = form.querySelector('input[name="properties[' + name + ']"]');
    if (!el) {
      el = document.createElement('input');
      el.type = 'hidden';
      el.name = 'properties[' + name + ']';
      form.appendChild(el);
    }
    return el;
  }
  function value(form, name) {
    var el = form.querySelector('input[name="properties[' + name + ']"], select[name="properties[' + name + ']"]');
    return el ? String(el.value || '').trim() : '';
  }
  // Ours to (re)write: empty, or still holding the value this script wrote last.
  function setAuto(form, name, val) {
    var el = form.querySelector('input[name="properties[' + name + ']"]');
    if (el && el.value && el.value !== el.dataset.rtaAuto) return;
    if (!val) {                       // no blank property on the order
      if (el && el.parentNode) el.parentNode.removeChild(el);
      return;
    }
    el = input(form, name);
    el.value = val || '';
    el.dataset.rtaAuto = el.value;
  }

  function stamp(form) {
    var idEl = form.querySelector('[name="id"]');
    if (!idEl) return;
    var info = catalog()[String(idEl.value)];
    if (!info) return;
    var spec = parse({ sku: info.sku, variantTitle: info.vt, productTitle: info.pt, productType: info.ty });
    var size = spec ? format(spec) : (/^default title$/i.test(info.vt) ? '' : info.vt);
    setAuto(form, 'Size', size);
    var picked = value(form, 'Front color') || value(form, 'Wood finish');
    setAuto(form, 'Color', picked || !info.color ? '' : info.color);
  }
  function stampAll() {
    data = null;                                    // quick-add modals add product data later
    document.querySelectorAll('form[action*="/cart/add"]').forEach(stamp);
  }

  function boot() {
    document.body.addEventListener('submit', function (e) {
      var f = e.target;
      if (f && f.matches && f.matches('form[action*="/cart/add"]')) { data = null; stamp(f); }
    }, true);
    document.addEventListener('change', function () { setTimeout(stampAll, 60); });
    stampAll();
    setInterval(stampAll, 1000);
    upgradeCart(document);
    new MutationObserver(function () { upgradeCart(document); })
      .observe(document.body, { childList: true, subtree: true });
  }

  // ---- cart fallback --------------------------------------------------------
  function upgradeCart(root) {
    root.querySelectorAll('[data-rta-size-sku]:not([data-rta-size-done])').forEach(function (el) {
      el.setAttribute('data-rta-size-done', '');
      var spec = parse({
        sku: el.getAttribute('data-rta-size-sku'),
        variantTitle: el.getAttribute('data-rta-size-vt'),
        productTitle: el.getAttribute('data-rta-size-pt'),
        productType: el.getAttribute('data-rta-size-ty')
      });
      var legacy = el.getAttribute('data-rta-size-prop');
      var text = (legacy && fromLegacy(legacy, spec)) || (spec && format(spec));
      if (text) el.textContent = text;
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
